import { useState } from 'react';
import { db } from '../db/client';

interface Props {
  orgId: string;
  currentUserId: string;
  onSwitchOrg: () => void;
}

export function SettingsTab({ orgId, currentUserId, onSwitchOrg }: Props) {
  const { isLoading, data, error } = db.useQuery({
    organizations: { $: { where: { id: orgId } }, owner: {}, memberships: { user: {} } },
  });

  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (isLoading) return <div className="uin-muted">Loading…</div>;
  if (error) return <div className="uin-error">{error.message}</div>;
  const org = data?.organizations?.[0];
  if (!org) return <div className="uin-muted">Not found.</div>;

  const isOwner = org.owner?.id === currentUserId;
  const myMembership = (org.memberships ?? []).find((m) => m.user?.id === currentUserId);

  async function rename() {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await db.transact([db.tx.organizations[orgId].update({ name: name.trim() })]);
      setName('');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function leaveOrg() {
    if (!myMembership) return;
    if (!confirm('Leave this organization?')) return;
    try {
      await db.transact([db.tx.memberships[myMembership.id].delete()]);
      onSwitchOrg();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function deleteOrg() {
    if (!confirm(`Delete "${org.name}" and all its comments? This cannot be undone.`)) return;
    try {
      const allComments = await db.queryOnce({
        comments: { $: { where: { 'organization.id': orgId } }, replies: {} },
      });
      const allMemberships = await db.queryOnce({
        memberships: { $: { where: { 'organization.id': orgId } } },
      });
      const replyDeletes = (allComments.data.comments ?? []).flatMap((c) =>
        (c.replies ?? []).map((r) => db.tx.replies[r.id].delete()),
      );
      await db.transact([
        ...replyDeletes,
        ...(allComments.data.comments ?? []).map((c) => db.tx.comments[c.id].delete()),
        ...(allMemberships.data.memberships ?? []).map((m) => db.tx.memberships[m.id].delete()),
        db.tx.organizations[orgId].delete(),
      ]);
      onSwitchOrg();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div>
      <div className="uin-section-title">Current organization</div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 500 }}>{org.name}</div>
        <div className="uin-muted">Created {new Date(org.createdAt).toLocaleDateString()}</div>
      </div>

      {err && <div className="uin-error">{err}</div>}

      {isOwner && (
        <>
          <div className="uin-section-title">Rename</div>
          <input
            className="uin-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={org.name}
          />
          <button className="uin-btn-secondary" onClick={rename} disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : 'Save name'}
          </button>

          <div className="uin-section-title">Danger</div>
          <button className="uin-btn-danger" onClick={deleteOrg}>
            Delete organization
          </button>
        </>
      )}

      {!isOwner && myMembership && (
        <>
          <div className="uin-section-title">Membership</div>
          <button className="uin-btn-danger" onClick={leaveOrg}>Leave organization</button>
        </>
      )}

      <div className="uin-section-title">Account</div>
      <button className="uin-btn-secondary" onClick={onSwitchOrg}>Switch organization</button>
    </div>
  );
}
